// src/pages/dashboard/Dashboard.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useNotifications } from '../contexts/NotificationContext';
import {
  ShoppingCart,
  Clock,
  BarChart3,
  CheckCircle,
  AlertCircle,
  Package,
  RotateCcw,
  TrendingUp,
  Warehouse,
  Users,
  ListTodo,
  FileText,
  ChefHat,
  Box,
  Truck,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import {
  ordersAPI,
  productionAssignmentsAPI,
  chefsAPI,
  branchesAPI,
  returnsAPI,
  inventoryAPI,
} from '../services/api';
import { formatDate } from '../utils/formatDate';
import { ProductSearchInput } from './NewOrder';
import { LoadingSpinner } from '../components/UI/LoadingSpinner';

// Interfaces (unchanged except adding receivedOrders)
interface Stats {
  totalOrders: number;
  pendingOrders: number;
  approvedOrders: number;
  inProductionOrders: number;
  inTransitOrders: number;
  deliveredOrders: number;
  readyToShip: number;
  receivedOrders: number; // ✅ إضافة الطلبات المستلمة
  totalOrderValue: number;
  completedTasks: number;
  inProgressTasks: number;
  totalReturns: number;
  pendingReturns: number;
  approvedReturns: number;
  rejectedReturns: number;
  averageOrderValue: number;
  totalInventoryValue?: number;
  lowStockItems?: number;
  chefUtilizationRate?: number;
}

interface Task {
  id: string;
  orderId: string;
  orderNumber: string;
  productId: string;
  productName: string;
  productNameEn?: string;
  quantity: number;
  unit: string;
  unitEn?: string;
  status: 'pending' | 'assigned' | 'in_progress' | 'completed';
  branchName: string;
  branchNameEn?: string;
  createdAt: string;
}

interface BranchPerformance {
  branchName: string;
  branchNameEn?: string;
  branchId: string;
  performance: number;
  totalOrders: number;
  completedOrders: number;
}

interface ChefPerformance {
  chefId: string;
  chefName: string;
  chefNameEn?: string;
  performance: number;
  totalTasks: number;
  completedTasks: number;
}

interface Order {
  id: string;
  orderNumber: string;
  branchName: string;
  branchNameEn?: string;
  branchId: string;
  items: Array<{
    _id: string;
    productId: string;
    productName: string;
    productNameEn?: string;
    quantity: number;
    price: number;
    department: { _id: string; name: string; nameEn?: string } | null;
    assignedTo?: { _id: string; username: string; name: string; nameEn?: string } | null;
    status: 'pending' | 'assigned' | 'in_progress' | 'completed';
    returnedQuantity?: number;
    returnReason?: string;
    returnReasonEn?: string;
  }>;
  status: 'pending' | 'approved' | 'in_production' | 'completed' | 'in_transit' | 'delivered' | 'cancelled';
  totalAmount: number;
  date: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  createdBy: string;
  createdAt: string;
}

interface Return {
  id: string;
  returnNumber: string;
  branchName: string;
  branchNameEn?: string;
  branchId: string;
  items: Array<{
    productId: string;
    productName: string;
    productNameEn?: string;
    quantity: number;
    reason: string;
    reasonEn?: string;
  }>;
  status: 'pending_approval' | 'approved' | 'rejected';
  createdByName: string;
  createdByNameEn?: string;
  reviewNotes?: string;
  reviewNotesEn?: string;
  createdAt: string;
}

interface Chef {
  _id: string;
  userId: string;
  username: string;
  name: string;
  nameEn?: string;
  department: Array<{ _id: string; name: string; nameEn?: string }> | null;
}

interface InventoryItem {
  _id: string;
  product: {
    _id: string;
    name: string;
    nameEn: string;
    code: string;
    unit: string;
    unitEn: string;
    department: { _id: string; name: string; nameEn: string } | null;
    displayName: string;
    displayUnit: string;
    price: number;
  } | null;
  branch: { _id: string; name: string; nameEn: string; displayName: string } | null;
  currentStock: number;
  pendingReturnStock: number;
  damagedStock: number;
  minStockLevel: number;
  maxStockLevel: number;
  status: 'low' | 'normal' | 'full';
}

interface ProductHistoryEntry {
  _id: string;
  date: string;
  type: 'delivery' | 'return_pending' | 'return_rejected' | 'return_approved' | 'sale' | 'adjustment';
  quantity: number;
  description: string;
}

interface FilterState {
  search: string;
}

// Loader Component
const Loader: React.FC = () => (
  <div className="flex justify-center items-center h-screen">
    <LoadingSpinner />
  </div>
);

// StatsCard Component - الخطوط صغيرة وواضحة
const StatsCard: React.FC<{ title: string; value: string; icon: React.FC; color: string; ariaLabel: string }> = React.memo(
  ({ title, value, icon: Icon, color, ariaLabel }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`p-3 bg-gradient-to-br from-${color}-50 to-${color}-100 rounded-lg border border-${color}-200 cursor-pointer 
                  hover:shadow-sm transition-all duration-200 text-xs`}
      aria-label={ariaLabel}
    >
      <div className="flex items-center gap-2">
        <div className={`p-1.5 bg-${color}-100 rounded-md`}>
          <Icon className={`w-4 h-4 text-${color}-700`} />
        </div>
        <div>
          <p className="text-[10px] text-gray-600 font-medium truncate max-w-[90px]">{title}</p>
          <p className={`text-xs font-bold text-${color}-800 truncate`}>{value}</p> {/* ✅ تم تغيير text-sm إلى text-xs */}
        </div>
      </div>
    </motion.div>
  )
);

// QuickActionCard Component - Smaller
const QuickActionCard: React.FC<{
  title: string;
  icon: React.FC;
  color: string;
  onClick: () => void;
  isRtl: boolean;
}> = ({ title, icon: Icon, color, onClick, isRtl }) => (
  <motion.button
    whileHover={{ scale: 1.02, y: -1 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`p-3 bg-gradient-to-br from-${color}-50 to-${color}-100 rounded-lg border border-${color}-200 
                flex flex-col items-center gap-1 hover:shadow-sm transition-all text-xs`}
  >
    <div className={`p-2 bg-${color}-100 rounded-full`}>
      <Icon className={`w-5 h-5 text-${color}-700`} />
    </div>
    <span className="font-medium text-gray-800 text-center">{title}</span>
  </motion.button>
);

// ChefDashboard Component
const ChefDashboard: React.FC<{
  stats: Stats;
  tasks: Task[];
  isRtl: boolean;
  language: string;
  handleStartTask: (taskId: string, orderId: string) => void;
  handleCompleteTask: (taskId: string, orderId: string) => void;
}> = React.memo(({ stats, tasks, isRtl, language, handleStartTask, handleCompleteTask }) => {
  const [filter, setFilter] = useState<FilterState>({ search: '' });
  const filteredTasks = useMemo(() => {
    const searchTerm = filter.search.toLowerCase();
    return tasks
      .filter(
        (task) =>
          (task.productName?.toLowerCase() || '').includes(searchTerm) ||
          (task.productNameEn?.toLowerCase() || task.productName?.toLowerCase() || '').includes(searchTerm) ||
          task.orderNumber.toLowerCase().includes(searchTerm)
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 6);
  }, [tasks, filter.search]);
  return (
    <div className="space-y-4 text-xs">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatsCard
          title={isRtl ? 'إجمالي الطلبات' : 'Total Orders'}
          value={stats.totalOrders.toString()}
          icon={ShoppingCart}
          color="amber"
          ariaLabel={isRtl ? 'إجمالي الطلبات' : 'Total Orders'}
        />
        <StatsCard
          title={isRtl ? 'المهام المكتملة' : 'Completed Tasks'}
          value={stats.completedTasks.toString()}
          icon={CheckCircle}
          color="green"
          ariaLabel={isRtl ? 'المهام المكتملة' : 'Completed Tasks'}
        />
        <StatsCard
          title={isRtl ? 'قيد التنفيذ' : 'In Progress'}
          value={stats.inProgressTasks.toString()}
          icon={Clock}
          color="blue"
          ariaLabel={isRtl ? 'المهام قيد التنفيذ' : 'In Progress Tasks'}
        />
        <StatsCard
          title={isRtl ? 'معلقة' : 'Pending'}
          value={stats.pendingOrders.toString()}
          icon={AlertCircle}
          color="red"
          ariaLabel={isRtl ? 'الطلبات/المهام المعلقة' : 'Pending Orders/Tasks'}
        />
      </div>
      <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 text-xs">
        <div className="flex flex-col sm:flex-row items-center justify-between mb-3 gap-2">
          <h3 className="font-bold text-gray-800 flex items-center gap-1.5">
            <ChefHat className="w-4 h-4 text-amber-600" />
            {isRtl ? 'أحدث المهام' : 'Latest Tasks'}
          </h3>
          <ProductSearchInput
            value={filter.search}
            onChange={(value) => setFilter({ search: value })}
            placeholder={isRtl ? 'ابحث...' : 'Search...'}
            className="w-full sm:w-48 rounded-md border-gray-300 focus:ring-1 focus:ring-amber-500 focus:border-transparent text-xs"
            ariaLabel={isRtl ? 'بحث المهام' : 'Search tasks'}
          />
        </div>
        <div className="space-y-2 overflow-y-auto max-h-80">
          <AnimatePresence>
            {filteredTasks.length === 0 ? (
              <div className="text-center py-6">
                <ChefHat className="w-8 h-8 mx-auto text-gray-300 mb-1" />
                <p className="text-gray-500 text-xs">{isRtl ? 'لا توجد مهام' : 'No tasks'}</p>
              </div>
            ) : (
              filteredTasks.map((task) => (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.15 }}
                  className="border border-amber-100 rounded-md p-3 bg-amber-50/50 hover:bg-amber-50 transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-xs text-gray-800 truncate">
                          {isRtl ? `طلب #${task.orderNumber}` : `Order #${task.orderNumber}`}
                        </h4>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            task.status === 'pending' || task.status === 'assigned'
                              ? 'bg-amber-100 text-amber-800'
                              : task.status === 'in_progress'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {isRtl
                            ? ({
                                pending: 'معلق',
                                assigned: 'معيّن',
                                in_progress: 'قيد التنفيذ',
                                completed: 'مكتمل',
                              }[task.status] || '---')
                            : task.status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </span>
                      </div>
                      <p className="text-xs text-gray-700 mt-0.5 truncate">
                        {task.quantity} × {isRtl ? task.productName : task.productNameEn || task.productName}
                      </p>
                
                      <p className="text-[10px] text-gray-400">
                        {isRtl ? `منذ: ${task.createdAt}` : `Created: ${task.createdAt}`}
                      </p>
                    </div>
                    <div className="ml-2 flex flex-col gap-1">
                      {(task.status === 'pending' || task.status === 'assigned') && (
                        <button
                          onClick={() => handleStartTask(task.id, task.orderId)}
                          className="bg-amber-600 hover:bg-amber-700 text-white px-2 py-1 rounded text-[10px] font-medium"
                          aria-label={isRtl ? 'بدء المهمة' : 'Start Task'}
                        >
                          {isRtl ? 'ابدأ' : 'Start'}
                        </button>
                      )}
                      {task.status === 'in_progress' && (
                        <button
                          onClick={() => handleCompleteTask(task.id, task.orderId)}
                          className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-[10px] font-medium"
                          aria-label={isRtl ? 'إكمال المهمة' : 'Complete Task'}
                        >
                          {isRtl ? 'أكمل' : 'Done'}
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
});

export const Dashboard: React.FC = () => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const { user } = useAuth();
  const { socket, isConnected } = useSocket();
  const { addNotification, refreshTasks } = useNotifications();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [chefs, setChefs] = useState<Chef[]>([]);
  const [branches, setBranches] = useState<{ _id: string; name: string; nameEn?: string }[]>([]);
  const [returns, setReturns] = useState<Return[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [history, setHistory] = useState<ProductHistoryEntry[]>([]);
  const [branchPerformance, setBranchPerformance] = useState<BranchPerformance[]>([]);
  const [chefPerformance, setChefPerformance] = useState<ChefPerformance[]>([]);
  const [stats, setStats] = useState<Stats>({
    totalOrders: 0,
    pendingOrders: 0,
    approvedOrders: 0,
    inProductionOrders: 0,
    inTransitOrders: 0,
    deliveredOrders: 0,
    readyToShip: 0,
    receivedOrders: 0, // ✅ تهيئة
    totalOrderValue: 0,
    completedTasks: 0,
    inProgressTasks: 0,
    totalReturns: 0,
    pendingReturns: 0,
    approvedReturns: 0,
    rejectedReturns: 0,
    averageOrderValue: 0,
    totalInventoryValue: 0,
    lowStockItems: 0,
    chefUtilizationRate: 0,
  });
  const [inventoryFilter, setInventoryFilter] = useState<FilterState>({ search: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const cache = useMemo(() => new Map<string, any>(), []);
  const cacheKey = useMemo(() => `${user?.id || user?._id}-${user?.role}`, [user]);

  const fetchDashboardData = useCallback(
    debounce(async (forceRefresh = false) => {
      if (!user?.id && !user?._id) {
        const msg = isRtl ? 'الوصول غير مصرح به' : 'Unauthorized access';
        setError(msg);
        setLoading(false);
        toast.error(msg, { position: isRtl ? 'top-left' : 'top-right' });
        addNotification({
          _id: `error-noUser-${Date.now()}`,
          type: 'error',
          message: msg,
          read: false,
          createdAt: new Date().toLocaleString(language),
          path: '/dashboard',
        });
        return;
      }
      if (!forceRefresh && cache.has(cacheKey)) {
        const cached = cache.get(cacheKey);
        setOrders(cached.orders);
        setTasks(cached.tasks);
        setChefs(cached.chefs);
        setBranches(cached.branches);
        setReturns(cached.returns);
        setInventory(cached.inventory || []);
        setHistory(cached.history || []);
        setBranchPerformance(cached.branchPerformance);
        setChefPerformance(cached.chefPerformance);
        setStats(cached.stats);
        setLoading(false);
        setIsInitialLoad(false);
        return;
      }
      setLoading(true);
      try {
        const query: Record<string, any> = { limit: 20 };
        let ordersResp: any[] = [];
        let tasksResp: any[] = [];
        let chefsResp: any[] = [];
        let branchesResp: any[] = [];
        let returnsResp: { returns: any[]; total: number } = { returns: [], total: 0 };
        let inventoryResp: any[] = [];
        let historyResp: any[] = [];
        if (user.role === 'chef') {
          const chefProfile = await chefsAPI.getByUserId(user.id || user._id).catch(() => null);
          const chefId = chefProfile?._id;
          if (chefId) {
            tasksResp = await productionAssignmentsAPI.getChefTasks(chefId, { limit: 20 }).catch(() => []);
          } else {
            tasksResp = [];
          }
        } else {
          if (user.role === 'branch') query.branch = user.id || user._id;
          if (user.role === 'production' && user.department?._id) query.departmentId = user.department._id;
          const promises = [
            ordersAPI.getAll(query).catch(() => []),
            user.role !== 'branch'
              ? productionAssignmentsAPI.getAllTasks(query).catch(() => [])
              : Promise.resolve([]),
            ['admin', 'production' , 'accountant'].includes(user.role) ? chefsAPI.getAll().catch(() => []) : Promise.resolve([]),
            ['admin', 'production' , 'accountant'].includes(user.role)
              ? branchesAPI.getAll().catch(() => [])
              : Promise.resolve([]),
            ['admin', 'production', 'branch' , 'accountant'].includes(user.role)
              ? returnsAPI.getAll(query).catch(() => ({ returns: [], total: 0 }))
              : Promise.resolve({ returns: [], total: 0 }),
          ];
          if (user.role === 'branch') {
            promises.push(
              inventoryAPI.getByBranch(user.branchId || user._id).catch(() => []),
              inventoryAPI.getHistory({ branchId: user.branchId || user._id, limit: 10 }).catch(() => [])
            );
          }
          const results = await Promise.allSettled(promises);
          ordersResp = results[0].status === 'fulfilled' ? results[0].value : [];
          tasksResp = results[1].status === 'fulfilled' ? results[1].value : [];
          chefsResp = results[2].status === 'fulfilled' ? results[2].value : [];
          branchesResp = results[3].status === 'fulfilled' ? results[3].value : [];
          returnsResp = results[4].status === 'fulfilled' ? results[4].value : { returns: [], total: 0 };
          if (user.role === 'branch') {
            inventoryResp = results[5]?.status === 'fulfilled' ? results[5].value : [];
            historyResp = results[6]?.status === 'fulfilled' ? results[6].value : [];
          }
        }

        const mappedOrders = ordersResp.map((order: any) => ({
          id: order._id || crypto.randomUUID(),
          orderNumber: order.orderNumber || (isRtl ? '---' : 'N/A'),
          branchName: order.branch?.name || (isRtl ? 'فرع غير معروف' : 'Unknown'),
          branchNameEn: order.branch?.nameEn || order.branch?.name || 'Unknown',
          branchId: order.branch?._id || 'unknown',
          items: (order.items || []).map((item: any) => ({
            _id: item._id || crypto.randomUUID(),
            productId: item.product?._id || 'unknown',
            productName: item.product?.name || (isRtl ? 'منتج' : 'Product'),
            productNameEn: item.product?.nameEn || item.product?.name || 'Product',
            quantity: Number(item.quantity) || 0,
            price: Number(item.price) || 0,
            department: item.product?.department || { _id: 'unknown', name: isRtl ? 'قسم' : 'Dept', nameEn: 'Dept' },
            status: item.status || 'pending',
            assignedTo: item.assignedTo
              ? {
                  _id: item.assignedTo._id,
                  username: item.assignedTo.username || '',
                  name: item.assignedTo.name || (isRtl ? 'طاهٍ' : 'Chef'),
                  nameEn: item.assignedTo.nameEn || item.assignedTo.name || 'Chef',
                }
              : null,
            returnedQuantity: Number(item.returnedQuantity) || 0,
            returnReason: item.returnReason || '',
            returnReasonEn: item.returnReasonEn || item.returnReason || '',
          })),
          status: order.status || 'pending',
          totalAmount: Number(order.totalAmount || order.totalPrice) || 0,
          date: formatDate(order.createdAt || new Date(), language),
          priority: order.priority || 'medium',
          createdBy: order.createdBy?.username || (isRtl ? '---' : 'N/A'),
          createdAt: formatDate(order.createdAt || new Date(), language),
        }));

        const mappedTasks = tasksResp.map((task: any) => ({
          id: task._id || crypto.randomUUID(),
          orderId: task.order?._id || 'unknown',
          orderNumber: task.order?.orderNumber || (isRtl ? '---' : 'N/A'),
          productId: task.product?._id || 'unknown',
          productName: task.product?.name || (isRtl ? 'منتج' : 'Product'),
          productNameEn: task.product?.nameEn || task.product?.name || 'Product',
          quantity: Number(task.quantity) || 0,
          unit: task.product?.unit || 'قطعة',
          unitEn: task.product?.unitEn || task.product?.unit || 'item',
          status: task.status || 'pending',
          branchName: task.order?.branch?.name || (isRtl ? 'فرع' : 'Branch'),
          branchNameEn: task.order?.branch?.nameEn || task.order?.branch?.name || 'Branch',
          createdAt: formatDate(task.createdAt || new Date(), language),
        }));

        const mappedChefs = chefsResp.map((chef: any) => ({
          _id: chef._id || crypto.randomUUID(),
          userId: chef.user?._id || chef._id,
          username: chef.user?.username || chef.username || (isRtl ? '---' : 'N/A'),
          name: chef.user?.name || chef.name || (isRtl ? 'طاهٍ' : 'Chef'),
          nameEn: chef.user?.nameEn || chef.name || 'Chef',
          department: Array.isArray(chef.department)
            ? chef.department
            : chef.department
            ? [chef.department]
            : [],
        }));

        const mappedBranches = branchesResp
          .map((branch: any) => ({
            _id: branch._id || crypto.randomUUID(),
            name: branch.name || (isRtl ? 'فرع' : 'Branch'),
            nameEn: branch.nameEn || branch.name || 'Branch',
          }))
          .sort((a: any, b: any) => a.name.localeCompare(b.name));

        const mappedReturns = returnsResp.returns.map((ret: any) => ({
          id: ret._id || crypto.randomUUID(),
          returnNumber: ret.returnNumber || (isRtl ? '---' : 'N/A'),
          branchName: ret.branch?.name || (isRtl ? 'فرع' : 'Branch'),
          branchNameEn: ret.branch?.nameEn || ret.branch?.name || 'Branch',
          branchId: ret.branch?._id || 'unknown',
          items: (ret.items || []).map((item: any) => ({
            productId: item.product?._id || 'unknown',
            productName: item.product?.name || (isRtl ? 'منتج' : 'Product'),
            productNameEn: item.product?.nameEn || item.product?.name || 'Product',
            quantity: Number(item.quantity) || 0,
            reason: item.reason || '',
            reasonEn: item.reasonEn || item.reason || '',
          })),
          status: ret.status || 'pending_approval',
          createdByName: ret.createdBy?.name || (isRtl ? '---' : 'N/A'),
          createdByNameEn: ret.createdBy?.nameEn || ret.createdBy?.name || 'N/A',
          reviewNotes: ret.reviewNotes || '',
          reviewNotesEn: ret.reviewNotesEn || ret.reviewNotes || '',
          createdAt: formatDate(ret.createdAt || new Date(), language),
        }));

        let mappedInventory: InventoryItem[] = [];
        let mappedHistory: ProductHistoryEntry[] = [];
        if (user.role === 'branch') {
          mappedInventory = inventoryResp.map((item: any) => ({
            _id: item._id,
            product: item.product
              ? {
                  _id: item.product._id,
                  name: item.product.name,
                  nameEn: item.product.nameEn || item.product.name,
                  code: item.product.code || '',
                  unit: item.product.unit || 'قطعة',
                  unitEn: item.product.unitEn || item.product.unit || 'item',
                  department: item.product.department || null,
                  displayName: isRtl ? item.product.name : item.product.nameEn || item.product.name,
                  displayUnit: isRtl ? item.product.unit || 'قطعة' : item.product.unitEn || item.product.unit || 'items',
                  price: item.product.price || 0,
                }
              : null,
            branch: item.branch,
            currentStock: item.currentStock || 0,
            pendingReturnStock: item.pendingReturnStock || 0,
            damagedStock: item.damagedStock || 0,
            minStockLevel: item.minStockLevel || 0,
            maxStockLevel: item.maxStockLevel || 0,
            status: item.status || 'normal',
          }));
          mappedHistory = historyResp
            .map((entry: any) => ({
              _id: entry._id,
              date: formatDate(entry.date || new Date(), language),
              type: entry.type || 'adjustment',
              quantity: Number(entry.quantity) || 0,
              description: (entry.description || '').replace(/×/g, 'x').trim(),
            }))
            .filter((entry, i, self) => i === self.findIndex((e) => e._id === entry._id && e.description === entry.description));
        }

        const branchPerf = mappedBranches
          .map((branch) => {
            const branchOrders = mappedOrders.filter((o) => o.branchId === branch._id);
            const total = branchOrders.length;
            const completed = branchOrders.filter((o) => ['completed', 'delivered'].includes(o.status)).length;
            return {
              branchName: branch.name,
              branchNameEn: branch.nameEn,
              branchId: branch._id,
              performance: total > 0 ? (completed / total) * 100 : 0,
              totalOrders: total,
              completedOrders: completed,
            };
          })
          .filter((b) => b.totalOrders > 0);

        const chefPerf = mappedChefs
          .map((chef) => {
            const chefTasks = mappedOrders.flatMap((order) =>
              (order.items || []).filter((item) => item.assignedTo?._id === chef.userId)
            ).map((item) => ({
              productId: item.productId,
              status: item.status,
            }));
            const total = chefTasks.length;
            const completed = chefTasks.filter((t) => t.status === 'completed').length;
            const perf = total > 0 ? (completed / total) * 100 : 0;
            return {
              chefId: chef._id,
              chefName: chef.name,
              chefNameEn: chef.nameEn,
              performance: perf,
              totalTasks: total,
              completedTasks: completed,
            };
          })
          .filter((c) => c.totalTasks > 0);

        const totalOrders = user.role === 'chef' ? mappedTasks.length : mappedOrders.length;
        const pendingOrders = user.role === 'chef'
          ? mappedTasks.filter((t) => ['pending', 'assigned'].includes(t.status)).length
          : mappedOrders.filter((o) => o.status === 'pending').length;
        const approvedOrders = mappedOrders.filter((o) => o.status === 'approved').length;
        const inProductionOrders = mappedOrders.filter((o) => o.status === 'in_production').length;
        const inTransitOrders = mappedOrders.filter((o) => o.status === 'in_transit').length;
        const deliveredOrders = mappedOrders.filter((o) => o.status === 'delivered').length;
        const readyToShip = mappedOrders.filter((o) => o.status === 'completed').length;
        const receivedOrders = deliveredOrders; // ✅ الطلبات المستلمة = المُسلّمة
        const totalOrderValue = mappedOrders.reduce((sum, o) => sum + o.totalAmount, 0);
        const completedTasks = mappedTasks.filter((t) => t.status === 'completed').length;
        const inProgressTasks = mappedTasks.filter((t) => t.status === 'in_progress').length;
        const totalReturns = mappedReturns.length;
        const pendingReturns = mappedReturns.filter((r) => r.status === 'pending_approval').length;
        const approvedReturns = mappedReturns.filter((r) => r.status === 'approved').length;
        const rejectedReturns = mappedReturns.filter((r) => r.status === 'rejected').length;
        const averageOrderValue = totalOrders > 0 ? totalOrderValue / totalOrders : 0;
        let totalInventoryValue = 0;
        let lowStockItems = 0;
        let chefUtilizationRate = 0;

        if (user.role === 'branch') {
          totalInventoryValue = mappedInventory.reduce((sum, i) => sum + i.currentStock * (i.product?.price || 0), 0);
          lowStockItems = mappedInventory.filter((i) => i.minStockLevel > 0 && i.currentStock <= i.minStockLevel).length; // ✅ تحسين: تجاهل min=0
        }

        if (['admin', 'production' ,'accountant'].includes(user.role)) {
          const activeChefs = chefPerf.filter((c) => c.totalTasks > 0).length;
          chefUtilizationRate = mappedChefs.length > 0 ? (activeChefs / mappedChefs.length) * 100 : 0;
        }

        const newData = {
          orders: mappedOrders,
          tasks: mappedTasks,
          chefs: mappedChefs,
          branches: mappedBranches,
          returns: mappedReturns,
          inventory: mappedInventory,
          history: mappedHistory,
          branchPerformance: branchPerf,
          chefPerformance: chefPerf,
          stats: {
            totalOrders,
            pendingOrders,
            approvedOrders,
            inProductionOrders,
            inTransitOrders,
            deliveredOrders,
            readyToShip,
            receivedOrders, // ✅ تضمين
            totalOrderValue,
            completedTasks,
            inProgressTasks,
            totalReturns,
            pendingReturns,
            approvedReturns,
            rejectedReturns,
            averageOrderValue,
            totalInventoryValue,
            lowStockItems,
            chefUtilizationRate,
          },
        };
        cache.set(cacheKey, newData);
        setOrders(newData.orders);
        setTasks(newData.tasks);
        setChefs(newData.chefs);
        setBranches(newData.branches);
        setReturns(newData.returns);
        setInventory(newData.inventory);
        setHistory(newData.history);
        setBranchPerformance(newData.branchPerformance);
        setChefPerformance(newData.chefPerformance);
        setStats(newData.stats);
        setError(null);
        setIsInitialLoad(false);
      } catch (err: any) {
        console.error('[Dashboard] Fetch Error:', err);
        const msg =
          err.response?.status === 403
            ? isRtl
              ? 'الوصول ممنوع'
              : 'Access Denied'
            : isRtl
            ? 'حدث خطأ في التحميل'
            : 'Failed to load dashboard';
        setError(msg);
        toast.error(msg, { position: isRtl ? 'top-left' : 'top-right', autoClose: 3000 });
        addNotification({
          _id: `dash-err-${Date.now()}`,
          type: 'error',
          message: msg,
          read: false,
          createdAt: new Date().toLocaleString(language),
          path: '/dashboard',
        });
      } finally {
        setLoading(false);
      }
    }, 300),
    [user, isRtl, language, cacheKey, addNotification]
  );

  useEffect(() => {
    fetchDashboardData();
    return () => fetchDashboardData.cancel();
  }, [fetchDashboardData]);

  useEffect(() => {
    if (refreshTasks) fetchDashboardData(true);
  }, [refreshTasks, fetchDashboardData]);

  useEffect(() => {
    if (!socket || !user || !isConnected) return;
    const handleError = (msg: string) => {
      addNotification({
        _id: `socket-err-${Date.now()}`,
        type: 'warning',
        message: msg,
        read: false,
        createdAt: formatDate(new Date(), language),
        path: '/dashboard',
      });
    };

    socket.on('connect_error', () => handleError(isRtl ? 'اتصال السوكت فشل' : 'Socket connection lost'));
    socket.on('taskAssigned', (data: any) => {
      if (!data.orderId || !data.taskId || !data.chefId || data.chefId !== (user.id || user._id)) return;
      if (user.role === 'chef') {
        setTasks((prev) => [
          {
            id: data.taskId,
            orderId: data.orderId,
            orderNumber: data.orderNumber || '---',
            productId: data.productId || 'unknown',
            productName: data.productName || 'Product',
            productNameEn: data.productNameEn || data.productName || 'Product',
            quantity: Number(data.quantity) || 0,
            unit: data.unit || 'قطعة',
            unitEn: data.unitEn || data.unit || 'item',
            status: 'assigned',
            branchName: data.branchName || 'Branch',
            branchNameEn: data.branchNameEn || data.branchName || 'Branch',
            createdAt: formatDate(new Date(), language),
          },
          ...prev.filter((t) => t.id !== data.taskId),
        ]);
      }
      fetchDashboardData(true);
    });
    socket.on('orderCompleted', () => fetchDashboardData(true));
    socket.on('itemStatusUpdated', () => fetchDashboardData(true));
    socket.on('returnCreated', () => fetchDashboardData(true));
    socket.on('returnStatusUpdated', () => fetchDashboardData(true));
    socket.on('inventoryUpdated', () => fetchDashboardData(true));

    return () => {
      socket.off('connect_error');
      socket.off('taskAssigned');
      socket.off('orderCompleted');
      socket.off('itemStatusUpdated');
      socket.off('returnCreated');
      socket.off('returnStatusUpdated');
      socket.off('inventoryUpdated');
    };
  }, [socket, user, isConnected, isRtl, language, addNotification, fetchDashboardData]);

  const handleStartTask = useCallback(
    async (taskId: string, orderId: string) => {
      if (!isConnected) {
        toast.warn(isRtl ? 'الاتصال غير متاح' : 'Offline');
        return;
      }
      try {
        await productionAssignmentsAPI.updateTaskStatus(orderId, taskId, { status: 'in_progress' });
        setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: 'in_progress' } : t)));
        toast.success(isRtl ? 'تم بدء المهمة' : 'Task started');
      } catch (err: any) {
        toast.error(err.message || (isRtl ? 'فشل في البدء' : 'Start failed'));
      }
    },
    [isConnected, isRtl]
  );

  const handleCompleteTask = useCallback(
    async (taskId: string, orderId: string) => {
      if (!isConnected) {
        toast.warn(isRtl ? 'الاتصال غير متاح' : 'Offline');
        return;
      }
      try {
        await productionAssignmentsAPI.updateTaskStatus(orderId, taskId, { status: 'completed' });
        setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: 'completed' } : t)));
        toast.success(isRtl ? 'تم الإكمال' : 'Task completed');
      } catch (err: any) {
        toast.error(err.message || (isRtl ? 'فشل في الإكمال' : 'Completion failed'));
      }
    },
    [isConnected, isRtl]
  );

  const filteredInventory = useMemo(() => {
    const term = inventoryFilter.search.toLowerCase();
    return inventory.filter((item) => {
      const name = isRtl ? item.product?.name : item.product?.nameEn || item.product?.name;
      return name?.toLowerCase().includes(term);
    });
  }, [inventory, inventoryFilter.search, isRtl]);

  const lowStockItemsList = useMemo(
    () =>
      filteredInventory
        .filter((i) => i.minStockLevel > 0 && i.currentStock <= i.minStockLevel) // ✅ مهم جداً
        .slice(0, 8),
    [filteredInventory]
  );

  const recentHistory = useMemo(
    () => history.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 8),
    [history]
  );

  const sortedPendingOrders = useMemo(
    () =>
      orders
        .filter((o) => ['pending', 'approved', 'in_production', 'in_transit'].includes(o.status))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 8),
    [orders]
  );

  const sortedLatestReturns = useMemo(
    () => returns.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 8),
    [returns]
  );

  const quickActions = useMemo(() => {
    if (!user) return [];
    const actions = [];
    switch (user.role) {
      case 'branch':
        actions.push(
          { title: isRtl ? 'الطلبات' : 'Orders', icon: ShoppingCart, color: 'amber', path: '/branch-orders' },
          { title: isRtl ? 'المرتجعات' : 'Returns', icon: RotateCcw, color: 'orange', path: '/branch-returns' },
          { title: isRtl ? 'المخزون' : 'Inventory', icon: Warehouse, color: 'purple', path: '/branch-inventory' },
          { title: isRtl ? 'المبيعات' : 'Sales', icon: TrendingUp, color: 'green', path: '/branch-sales' }
        );
        break;
      case 'admin':
      case 'production':
        actions.push(
          { title: isRtl ? 'الطلبات' : 'Orders', icon: ShoppingCart, color: 'amber', path: '/orders' },
          { title: isRtl ? 'المرتجعات' : 'Returns', icon: RotateCcw, color: 'orange', path: '/returns' },
          { title: isRtl ? 'التقارير' : 'Reports', icon: FileText, color: 'blue', path: '/reports' },
          { title: isRtl ? 'المنتجات' : 'Products', icon: Box, color: 'indigo', path: '/products' }
        );
        break;
      case 'accountant':
        actions.push(
          { title: isRtl ? 'الطلبات' : 'Orders', icon: ShoppingCart, color: 'amber', path: '/orders' },
          { title: isRtl ? 'المرتجعات' : 'Returns', icon: RotateCcw, color: 'orange', path: '/returns' },
          { title: isRtl ? 'التقارير ' : 'Financial Reports', icon: FileText, color: 'blue', path: '/financial-reports' },
                    { title: isRtl ? 'المبيعات' : 'Sales', icon: TrendingUp, color: 'green', path: '/branch-sales' }
        );
        break;
      case 'chef':
        break;
    }
    return actions;
  }, [user, isRtl]);

  const renderStats = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-2.5 mb-4 text-xs"
    >
    
    
    
      <>
    
    
        {user && ['admin', 'production', 'branch' , 'accountant'].includes(user.role) && (
          
          <>   
          
          <StatsCard
        title={isRtl ? 'إجمالي الطلبات' : 'Total Orders'}
        value={stats.totalOrders.toLocaleString()}
        icon={ShoppingCart}
        color="amber"
        ariaLabel="Total Orders"
      />
      <StatsCard
        title={isRtl ? 'معلقة' : 'Pending'}
        value={stats.pendingOrders.toLocaleString()}
        icon={AlertCircle}
        color="red"
        ariaLabel="Pending Orders"
      />
      <StatsCard
        title={isRtl ? 'معتمدة' : 'Approved'}
        value={stats.approvedOrders.toLocaleString()}
        icon={CheckCircle}
        color="blue"
        ariaLabel="Approved Orders"
      />
     
      <StatsCard
        title={isRtl ? 'جاهز للشحن' : 'Ready to Ship'}
        value={stats.readyToShip.toLocaleString()}
        icon={Package}
        color="teal"
        ariaLabel="Ready to Ship"
      />
      <StatsCard
        title={isRtl ? 'قيد النقل' : 'In Transit'}
        value={stats.inTransitOrders.toLocaleString()}
        icon={Truck}
        color="purple"
        ariaLabel="In Transit"
      />
      <StatsCard
        title={isRtl ? 'مكتملة' : 'Delivered'}
        value={`${stats.deliveredOrders} / ${stats.totalOrders}`}
        icon={CheckCircle}
        color="green"
        ariaLabel="Completed Orders"
      />
      <StatsCard
        title={isRtl ? 'تم التسليم' : 'Received'}
        value={stats.receivedOrders.toLocaleString()}
        icon={CheckCircle}
        color="emerald"
        ariaLabel="Received Orders"
      />
      <StatsCard
        title={isRtl ? 'متوسط قيمه الطلب' : 'Avg. Order Value'}
        value={`${stats.averageOrderValue.toFixed(2)} ${isRtl ? 'ر.س' : 'SAR'}`}
        icon={BarChart3}
        color="teal"
        ariaLabel="Average Order Value"
      />
      <StatsCard
            title={isRtl ? 'مرفوضة' : 'Rejected'}
            value={stats.rejectedReturns.toLocaleString()}
            icon={AlertCircle}
            color="red"
            ariaLabel="Rejected Returns"
          />
  
      
          <StatsCard
            title={isRtl ? 'المرتجعات' : 'Returns'}
            value={stats.totalReturns.toLocaleString()}
            icon={RotateCcw}
            color="orange"
            ariaLabel="Total Returns"
          />
          <StatsCard
            title={isRtl ? 'قيد المراجعة' : 'Pending'}
            value={stats.pendingReturns.toLocaleString()}
            icon={AlertCircle}
            color="red"
            ariaLabel="Pending Returns"
          />
          <StatsCard
            title={isRtl ? 'تمت الموافقة' : 'Approved'}
            value={stats.approvedReturns.toLocaleString()}
            icon={CheckCircle}
            color="green"
            ariaLabel="Approved Returns"
          />
          <StatsCard
            title={isRtl ? 'مرفوضة' : 'Rejected'}
            value={stats.rejectedReturns.toLocaleString()}
            icon={AlertCircle}
            color="red"
            ariaLabel="Rejected Returns"
          />

         
          
  
        </>


   )} 

      </>
      {user.role === 'branch' && (
        <>
          <StatsCard
            title={isRtl ? 'قيمة المخزون' : 'Inv. Value'}
            value={`${stats.totalInventoryValue?.toFixed(0) || '0'} ${isRtl ? 'ر.س' : 'SAR'}`}
            icon={Warehouse}
            color="purple"
            ariaLabel="Inventory Value"
          />
           <StatsCard
        title={isRtl ? 'معلقة' : 'Pending'}
        value={stats.pendingOrders.toLocaleString()}
        icon={AlertCircle}
        color="red"
        ariaLabel="Pending Orders"
      />
          
        </>
      )}
      {['admin', 'production' , 'accountant'].includes(user.role) && (
        <>
          <StatsCard
            title={isRtl ? 'المهام المكتملة' : 'Completed Tasks'}
            value={stats.completedTasks.toLocaleString()}
            icon={CheckCircle}
            color="green"
            ariaLabel="Completed Tasks"
          />
          <StatsCard
            title={isRtl ? 'قيد التنفيذ' : 'In Progress'}
            value={stats.inProgressTasks.toLocaleString()}
            icon={Clock}
            color="blue"
            ariaLabel="In Progress Tasks"
          />
          <StatsCard
            title={isRtl ? 'اكمال الانتاج  ' : 'Production Completion'}
            value={`${stats.chefUtilizationRate?.toFixed(1) || '0'}%`}
            icon={Users}
            color="indigo"
            ariaLabel="Chef Utilization"
          />
        </>
      )}
    </motion.div>
  );

 const renderLatestOrders = () => {
  const getOrderPath = (orderId: string) => {
    if (user?.role === 'branch') {
      return `/branch-orders/${orderId}`;
    }
    // admin, production, أو أي دور إداري
    return `/orders/${orderId}`;
  };

  return (
    <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 text-xs">
      <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-1.5">
        <ShoppingCart className="w-4 h-4 text-amber-600" />
        {isRtl ? 'أحدث الطلبات' : 'Latest Orders'}
      </h3>
      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
        {sortedPendingOrders.length === 0 ? (
          <p className="text-gray-500 text-center py-4">{isRtl ? 'لا توجد طلبات' : 'No orders'}</p>
        ) : (
          sortedPendingOrders.map((order) => (
            <motion.div
              key={order.id}
              whileHover={{ x: isRtl ? 3 : -3 }}
              className="p-2.5 border border-gray-100 rounded-md bg-gray-50 hover:bg-amber-50 cursor-pointer transition-colors"
              onClick={() => navigate(getOrderPath(order.id))}
            >
              <div className="flex justify-between items-start">
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 truncate text-xs">
                    {isRtl ? `طلب #${order.orderNumber}` : `Order #${order.orderNumber}`}
                  </p>
                  <p className="text-[10px] text-gray-600 truncate">
                    {isRtl ? order.branchName : order.branchNameEn}
                  </p>
                </div>
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    order.status === 'pending'
                      ? 'bg-amber-100 text-amber-800'
                      : order.status === 'approved'
                      ? 'bg-blue-100 text-blue-800'
                      : order.status === 'in_production'
                      ? 'bg-blue-100 text-blue-800'
                      : order.status === 'in_transit'
                      ? 'bg-purple-100 text-purple-800'
                      : order.status === 'completed'
                      ? 'bg-teal-100 text-teal-800'
                      : 'bg-green-100 text-green-800'
                  }`}
                >
                  {isRtl
                    ? ({
                        pending: 'معلق',
                        approved: 'معتمد',
                        in_production: 'إنتاج',
                        in_transit: 'في الطريق',
                        completed: 'جاهز',
                        delivered: 'تم التسليم',
                        cancelled: 'ملغى',
                      }[order.status] || order.status)
                    : order.status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </span>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                {isRtl ? `منذ: ${order.createdAt}` : `Created: ${order.createdAt}`}
              </p>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

  const renderLatestReturns = () => (
    <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 text-xs">
      <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-1.5">
        <RotateCcw className="w-4 h-4 text-orange-600" />
        {isRtl ? 'أحدث المرتجعات' : 'Latest Returns'}
      </h3>
      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
        {sortedLatestReturns.length === 0 ? (
          <p className="text-gray-500 text-center py-4">{isRtl ? 'لا توجد مرتجعات' : 'No returns'}</p>
        ) : (
          sortedLatestReturns.map((ret) => (
            <motion.div
              key={ret.id}
              whileHover={{ x: isRtl ? 3 : -3 }}
              className="p-2.5 border border-gray-100 rounded-md bg-gray-50 hover:bg-orange-50 cursor-pointer transition-colors"
           /*   onClick={() => navigate(`/returns/${ret.id}`)} */ 
            >
              <div className="flex justify-between items-start">
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 truncate text-xs">
                    {isRtl ? `مرتجع #${ret.returnNumber}` : `Return #${ret.returnNumber}`}
                  </p>
                  <p className="text-[10px] text-gray-600 truncate">
                    {isRtl ? ret.branchName : ret.branchNameEn}
                  </p>
                </div>
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    ret.status === 'pending_approval'
                      ? 'bg-amber-100 text-amber-800'
                      : ret.status === 'approved'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {isRtl
                    ? ({
                        pending_approval: 'معلق',
                        approved: 'موافق',
                        rejected: 'مرفوض',
                      }[ret.status] || ret.status)
                    : ret.status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </span>
              </div>
              <p className="text-[10px] text-gray-500 mt-1">
                {isRtl ? `منذ: ${ret.createdAt}` : `Created: ${ret.createdAt}`}
              </p>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );

  const renderLowStockItems = () => (
    <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 text-xs">
      <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-1.5">
        <AlertCircle className="w-4 h-4 text-red-600" />
        {isRtl ? 'انخفاض المخزون' : 'Low Stock'}
      </h3>
      <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
        {lowStockItemsList.length === 0 ? (
          <p className="text-gray-500 text-center py-4">{isRtl ? 'كل المخزون بحالة جيدة' : 'All stock is sufficient'}</p>
        ) : (
          lowStockItemsList.map((item, i) => (
            <motion.div
              key={item._id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: i * 0.03 }}
              className="flex justify-between items-end p-2 bg-red-50 rounded-md"
            >
              <div>
                <p className="font-medium text-gray-800 text-xs">{item.product?.displayName}</p>
                <p className="text-[10px] text-red-700">
                  {isRtl ? 'المتاح:' : 'Available:'} {item.currentStock} / {item.minStockLevel}
                </p>
              </div>
              <span className="bg-red-100 text-red-800 text-[10px] px-1.5 py-0.5 rounded-full">
                {isRtl ? 'يحتاج تجديد' : 'Reorder'}
              </span>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );

  const renderRecentInventoryHistory = () => (
    <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 text-xs">
      <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-1.5">
        <Clock className="w-4 h-4 text-gray-600" />
        {isRtl ? 'سجل المخزون' : 'Inventory Log'}
      </h3>
      <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
        {recentHistory.length === 0 ? (
          <p className="text-gray-500 text-center py-4">{isRtl ? 'لا توجد عمليات' : 'No activity'}</p>
        ) : (
          recentHistory.map((entry, i) => (
            <motion.div
              key={entry._id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: i * 0.03 }}
              className="flex justify-between items-end p-2 bg-gray-50 rounded-md border border-gray-100"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 truncate text-xs">
                  {isRtl
                    ? ({
                        delivery: 'توصيل',
                        sale: 'بيع',
                        adjustment: 'تعديل',
                        return_pending: 'مرتجع (قيد)',
                        return_approved: 'مرتجع (موافق)',
                        return_rejected: 'مرتجع (مرفوض)',
                      }[entry.type] || entry.type)
                    : entry.type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </p>
                <p className="text-[10px] text-gray-600 truncate">{entry.description}</p>
              </div>
              <div className={` ${isRtl ? 'text-left' : 'text-right'} text-[10px] font-medium text-gray-800 whitespace-nowrap`}>
                {entry.quantity > 0 ? '+' : ''}
                {entry.quantity} {isRtl ? 'قطع' : 'items'}
                <br />
                <span className="text-[9px] text-gray-500">{entry.date}</span>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );

  const renderBranchPerformance = () => (
    <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 text-xs">
      <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-1.5">
        <BarChart3 className="w-4 h-4 text-amber-600" />
        {isRtl ? 'أداء الفروع' : 'Branch Performance'}
      </h3>
      <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
        {branchPerformance.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            <BarChart3 className="w-6 h-6 mx-auto text-gray-300 mb-1" />
            {isRtl ? 'لا توجد بيانات' : 'No data'}
          </div>
        ) : (
          branchPerformance.map((branch, i) => (
            <motion.div
              key={branch.branchId}
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.03 }}
              className="flex items-center justify-between p-2 bg-gray-50 rounded-md hover:bg-amber-50 cursor-pointer"
           /*   onClick={() => navigate(`/branches/${branch.branchId}`)} */ 
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 truncate text-xs">{isRtl ? branch.branchName : branch.branchNameEn}</p>
                <p className="text-[10px] text-gray-500">
                  {isRtl ? `${branch.completedOrders}/${branch.totalOrders}` : `${branch.completedOrders}/${branch.totalOrders}`}
                </p>
              </div>
              <div className="flex items-center gap-2 w-24">
                <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-amber-500 h-1.5 rounded-full"
                    style={{ width: `${Math.min(branch.performance, 100)}%` }}
                  ></div>
                </div>
                <span className="text-[10px] font-medium text-amber-700 w-8 text-right">{branch.performance.toFixed(0)}%</span>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );

  const renderChefPerformance = () => (
    <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200 text-xs">
      <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-1.5">
        <ChefHat className="w-4 h-4 text-amber-600" />
        {isRtl ? 'أداء الطهاة' : 'Chef Performance'}
      </h3>
      <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
        {chefPerformance.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            <ChefHat className="w-6 h-6 mx-auto text-gray-300 mb-1" />
            {isRtl ? 'لا توجد بيانات' : 'No data'}
          </div>
        ) : (
          chefPerformance.map((chef, i) => (
            <motion.div
              key={chef.chefId}
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.03 }}
              className="flex items-center justify-between p-2 bg-gray-50 rounded-md hover:bg-amber-50 cursor-pointer"
       /*       onClick={() => navigate(`/chefs/${chef.chefId}`)} */ 
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 truncate text-xs">{isRtl ? chef.chefName : chef.chefNameEn}</p>
                <p className="text-[10px] text-gray-500">
                  {isRtl ? `${chef.completedTasks}/${chef.totalTasks}` : `${chef.completedTasks}/${chef.totalTasks}`}
                </p>
              </div>
              <div className="flex items-center gap-2 w-24">
                <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-green-500 h-1.5 rounded-full"
                    style={{ width: `${Math.min(chef.performance, 100)}%` }}
                  ></div>
                </div>
                <span className="text-[10px] font-medium text-green-700 w-8 text-right">{chef.performance.toFixed(0)}%</span>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );

  if (loading && isInitialLoad) return <Loader />;
  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
        <h2 className="text-lg font-bold text-gray-800 mb-2">{error}</h2>
        <button
          onClick={() => fetchDashboardData(true)}
          className="mt-3 px-3.5 py-1.5 bg-amber-600 text-white rounded-md text-sm hover:bg-amber-700 transition-colors"
        >
          {isRtl ? 'إعادة المحاولة' : 'Retry'}
        </button>
      </div>
    );
  }

  return (
    <div className={`mx-auto px-3 py-4 max-w-none ${isRtl ? 'rtl' : 'ltr'} text-xs`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-amber-600" />
          {isRtl ? 'لوحة التحكم' : 'Dashboard'}
        </h1>
      </div>
      {renderStats()}
      {user.role === 'chef' ? (
        <ChefDashboard
          stats={stats}
          tasks={tasks}
          isRtl={isRtl}
          language={language}
          handleStartTask={handleStartTask}
          handleCompleteTask={handleCompleteTask}
        />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderLatestOrders()}
            {user && ['admin', 'production', 'branch' , 'accountant'].includes(user.role) && renderLatestReturns()}
            {user && user.role === 'branch' && renderLowStockItems()}
            {user && user.role === 'branch' && renderRecentInventoryHistory()}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {user && ['admin', 'production' , 'accountant'].includes(user.role) && renderBranchPerformance()}
            {user && ['admin', 'production' , 'accountant'].includes(user.role) && renderChefPerformance()}
          </div>
        </div>
      )}
      {user && user.role !== 'chef' && quickActions.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <h2 className="font-bold text-gray-800 mb-3 flex  items-center justify-center md:justify-start gap-1.5">
            <ListTodo className="w-4 h-4 text-gray-600" />
            {isRtl ? 'إجراءات سريعة' : 'Quick Actions'}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {quickActions.map((action, i) => (
              <QuickActionCard
                key={i}
                title={action.title}
                icon={action.icon}
                color={action.color}
                onClick={() => navigate(action.path)}
                isRtl={isRtl}
              />
            ))}
          </div>
        </div>
      )}
      <div className="h-8" />
    </div>
  );
};

export default Dashboard;